// BillsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './BillsPage.css';

export default function BillsPage() {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch bills with optional status filter
  const fetchBills = async () => {
    setLoading(true);
    try {
      const query = filterStatus ? `?status=${filterStatus}` : '';
      const { data } = await api.get(`/bills${query}`);
      setBills(data);
    } catch (error) {
      console.error('Error fetching bills:', error);
      setBills([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBills(); }, [filterStatus]);

  const handleNew = async () => {
    try {
      const { data } = await api.post('/bills', {});
      navigate(`/bills/${data.billId}`);
    } catch (error) {
      console.error('Error creating bill:', error);
      alert('Failed to create bill');
    }
  };

  const handleCancel = async (billId) => {
    if (!window.confirm('Cancel this bill?')) return;
    try {
      await api.patch(`/bills/${billId}/cancel`);
      fetchBills();
    } catch (error) {
      console.error('Error cancelling bill:', error);
      alert('Failed to cancel bill');
    }
  };

  const handleFinalize = async (billId, currentTotal) => {
    const tax = prompt('Enter tax amount:', '0');
    const discount = prompt('Enter discount amount:', '0');
    const defaultTotal = (parseFloat(currentTotal) || 0) + (parseFloat(tax) || 0) - (parseFloat(discount) || 0);
    const total = prompt('Enter final total:', defaultTotal.toFixed(2));
    const paymentMode = prompt('Payment mode (Cash/Card/UPI):', 'Cash');
    if (total == null || paymentMode == null) return;
    try {
      await api.patch(`/bills/${billId}/finalize`, {
        tax: parseFloat(tax) || 0,
        discount: parseFloat(discount) || 0,
        total: parseFloat(total) || 0,
        paymentMode
      });
      fetchBills();
    } catch (error) {
      console.error('Error finalizing bill:', error);
      alert('Failed to finalize bill');
    }
  };

  // const handleReceipt = (billId) => navigate(`/bills/${billId}`);
const handleReceipt = (billId) => navigate(`/bills/${billId}/print`);
const formatDate = (iso) => {
  // Format date as DD/MM/YY
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

  // Apply filter and search
  const filtered = bills.filter(b => {
    const term = searchTerm.toLowerCase();
    return (
      (!term ||
      b.BillID.toString().includes(term) ||
      b.Status.toLowerCase().includes(term) ||
      formatDate(b.CreatedAt).includes(term))
    );
  });

  // Pagination
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const pageData = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Generate PDF using jsPDF AutoTable
  const downloadPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait' });
    const head = [['Bill ID', 'Status', 'Date', 'Total']];
    const body = filtered.map(b => [
      `#${b.BillID}`,
      b.Status.charAt(0).toUpperCase() + b.Status.slice(1),
      formatDate(b.CreatedAt),
      b.Status === 'paid' ? `Rs ${(+b.Total).toFixed(2)}` : 'Rs 0.00'
    ]);
    doc.text('Bills Report', 14, 15);
    autoTable(doc, { head, body, startY: 20 });
    doc.save('bills_report.pdf');
  };

  if (loading) return <div className="page-container">Loading…</div>;

  return (
    <div className="page-container">
      <div className="header-row">
        <button className="btn-back" onClick={() => navigate(-1)}>← Back</button>
        <h2>Bills</h2>
      </div>

      <div className="top-actions">
        <button className="btn-primary" onClick={handleNew}>+ New Bill</button>
        <div className="controls">
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="open">Open</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <input
            type="text"
            placeholder="Search ID, status or date"
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          />
          <button className="btn-secondary" onClick={downloadPDF}>Download PDF</button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="no-bills">No bills to display.</p>
      ) : (
        <>
          <div className="table-container">
            <table className="bills-table">
              <thead>
                <tr>
                  <th>Bill ID</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Total</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageData.map(b => (
                  <tr key={b.BillID}>
                    <td><button className="link-button" onClick={() => navigate(`/bills/${b.BillID}`)}>#{b.BillID}</button></td>
                    <td>{b.Status.charAt(0).toUpperCase() + b.Status.slice(1)}</td>
                    <td>{formatDate(b.CreatedAt)}</td>
                    <td>{b.Status === 'paid' ? `Rs ${(+b.Total).toFixed(2)}` : 'Rs 0.00'}</td>
                    <td>
                      {b.Status === 'draft' && <><button onClick={() => navigate(`/bills/${b.BillID}`)}>Edit</button><button onClick={() => handleCancel(b.BillID)}>Cancel</button></>}
                      {b.Status === 'open' && <><button onClick={() => navigate(`/bills/${b.BillID}`)}>View</button><button onClick={() => handleFinalize(b.BillID, b.Total)}>Finalize</button></>}
                      {b.Status === 'paid' && <button onClick={() => handleReceipt(b.BillID)}>Receipt</button>}
                      {b.Status === 'cancelled' && <button onClick={() => navigate(`/bills/${b.BillID}`)}>View</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>Prev</button>
            <span>Page {currentPage} of {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>Next</button>
          </div>
        </>
      )}
    </div>
  );
}

 